import { FC } from 'react';
import { Typography, Button, theme, Drawer } from 'antd';
import { InboxOutlined, EditOutlined, LinkOutlined, EnvironmentOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Timestamp } from 'firebase/firestore';
import dayjs from 'dayjs';
import { ShippingTracker } from '#types';
import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';
import FlexCard from '@/components/atoms/FlexCard';
import { useShippingTrackerUI } from './useShippingTrackerUI';
import TrackingHistoryTimeline from './TrackingHistoryTimeline';
import DetailedTrackingForm from './DetailedTrackingForm';
import FlexDivider from '@/components/atoms/FlexDivider';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import { resolveTrackingUrlForShippingValue } from './resolveTrackingUrlForShippingValue';

const { Text } = Typography;

interface ShippingTrackerExpandedUIProps {
    value: string | ShippingTracker | null | undefined;
    onChange: (value: ShippingTracker | null) => void;
    disabled?: boolean;
    status?: '' | 'error' | 'warning' | undefined;
    fieldId?: string;
    [key: string]: any;
}

const ShippingTrackerExpandedUI: FC<ShippingTrackerExpandedUIProps> = ({
    value,
    onChange,
    disabled = false,
    status,
    fieldId,
    ...props
}) => {
    const {
        trackerValue,
        drawerOpen,
        setDrawerOpen,
        handleComponentChange,
        getCarrierInfo,
        refreshFromProvider,
        isRefreshing,
        refreshError,
    } = useShippingTrackerUI({ value, onChange });

    const { isPdfExportMode } = usePdfExportContext();
    const { token } = theme.useToken();

    const components = trackerValue?.components;
    const carrier = getCarrierInfo(components?.carrierId || 'other');
    const trackingUrl = resolveTrackingUrlForShippingValue(trackerValue);
    const pdfExportLinkText = [carrier?.name, components?.trackingNumber]
        .filter((x) => (x ?? '').toString().trim())
        .join(' ');

    const deliveryDateText = components?.deliveryDate instanceof Timestamp
        ? dayjs(components.deliveryDate.toDate()).format('MMMM D, YYYY')
        : 'Not specified';

    const hasData = components?.trackingNumber;

    if (!hasData) {
        return (
            <FlexCard
                style={{
                    border: `1px solid ${status === 'error' ? token.colorError : token.colorBorder}`,
                    ...props.style
                }}
                aria-label="shipping-tracker-expanded-empty"
            >
                <Button
                    type="link"
                    icon={<PlusOutlined />}
                    disabled={disabled}
                    onClick={() => setDrawerOpen(true)}
                    aria-label="add-tracking-info"
                >
                    Add Tracking Info
                </Button>
                <Drawer
                    title="Shipping Tracker Details"
                    placement="right"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    styles={{
                        wrapper: { width: 500, maxWidth: '100%' },
                        body: {
                            padding: token.padding,
                            overflowY: 'auto'
                        }
                    }}
                >
                    <DetailedTrackingForm
                        trackerValue={trackerValue}
                        disabled={disabled}
                        status={status}
                        onComponentChange={(comps) => {
                            handleComponentChange(comps);
                            setDrawerOpen(false);
                        }}
                        onCancel={() => setDrawerOpen(false)}
                    />
                </Drawer>
            </FlexCard>
        );
    }

    return (
        <FlexCard
            style={{
                width: 400,
                maxWidth: '100%',
                alignSelf: 'flex-start',
                border: `1px solid ${status === 'error' ? token.colorError : token.colorBorder}`,
                ...props.style
            }}
            aria-label="shipping-tracker-expanded-display"
        >
            <FlexCol>
                <FlexBox gap={token.paddingXL} wrap>
                    <FlexBox>
                        <InboxOutlined style={{ fontSize: 24, color: token.colorPrimary }} />
                        <FlexCol noGap>
                            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>Carrier</Text>
                            <Text strong style={{ fontSize: 16 }}>{carrier?.name}</Text>
                        </FlexCol>
                    </FlexBox>
                    <FlexCol noGap>
                        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>Tracking Number</Text>
                        <Text strong style={{ fontSize: 16 }}>{components?.trackingNumber}</Text>
                    </FlexCol>
                    {components?.currentStatus && (
                        <FlexCol noGap>
                            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>Current Status</Text>
                            <Text strong>{components.currentStatus}</Text>
                        </FlexCol>
                    )}
                    <FlexCol noGap>
                        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>Delivery Date</Text>
                        <Text>{deliveryDateText}</Text>
                    </FlexCol>
                    {components?.origin && (
                        <FlexCol noGap>
                            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>Origin</Text>
                            <FlexBox gap={4}>
                                <EnvironmentOutlined />
                                <Text>{components.origin}</Text>
                            </FlexBox>
                        </FlexCol>
                    )}
                    {components?.destination && (
                        <FlexCol noGap>
                            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>Destination</Text>
                            <FlexBox gap={4}>
                                <EnvironmentOutlined />
                                <Text>{components.destination}</Text>
                            </FlexBox>
                        </FlexCol>
                    )}
                </FlexBox>
                {isPdfExportMode && trackingUrl && pdfExportLinkText ? (
                    <Text style={{ fontSize: token.fontSizeSM }}>
                        <a
                            href={trackingUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: token.colorText }}
                            aria-label={fieldId ? `tracking-url-pdf-expanded-${fieldId}` : 'tracking-url-pdf-expanded'}
                        >
                            {pdfExportLinkText}
                        </a>
                    </Text>
                ) : null}

                {components?.trackingHistory && components.trackingHistory.length > 0 && (
                    <FlexCol gap={0}>
                        <FlexDivider spacing={10} />
                        <Text>Tracking History</Text>
                        <TrackingHistoryTimeline trackingHistory={components.trackingHistory} />
                    </FlexCol>
                )}

                {!disabled && (
                    <FlexBox>
                        {trackingUrl && (
                            <Button
                                type="link"
                                size="small"
                                icon={<LinkOutlined />}
                                href={trackingUrl}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="open-tracking-url"
                            >
                                Track Online
                            </Button>
                        )}
                        <Button
                            type="link"
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => void refreshFromProvider({ trackingNumber: components?.trackingNumber || '', carrierId: components?.carrierId || 'other', force: true })}
                            disabled={disabled || isRefreshing}
                            loading={isRefreshing}
                            aria-label="refresh-tracking"
                        >
                            Refresh
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => setDrawerOpen(true)}
                            aria-label="edit-tracking-info"
                        >
                            Edit
                        </Button>
                    </FlexBox>
                )}
                <Drawer
                    title="Shipping Tracker Details"
                    placement="right"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    styles={{
                        wrapper: { width: 500, maxWidth: '100%' },
                        body: {
                            padding: token.padding,
                            overflowY: 'auto'
                        }
                    }}
                >
                    <DetailedTrackingForm
                        trackerValue={trackerValue}
                        disabled={disabled}
                        status={status}
                        onComponentChange={(comps) => {
                            handleComponentChange(comps);
                            setDrawerOpen(false);
                        }}
                        onRefresh={async ({ trackingNumber: tn, carrierId: cid, force }) => {
                            await refreshFromProvider({ trackingNumber: tn, carrierId: cid, force });
                            setDrawerOpen(false);
                        }}
                        isRefreshing={isRefreshing}
                        refreshError={refreshError}
                        onCancel={() => setDrawerOpen(false)}
                    />
                </Drawer>
            </FlexCol>
        </FlexCard>
    );
};

export default ShippingTrackerExpandedUI;

